import React, { Suspense, lazy, Component } from 'react';
import { BaseWidgetProps } from './types';
// ExamModeWidget imported statically — avoids lazy-load chunk failures in React 19 for HSC/VCE
import { ExamModeWidget } from './ExamMode/ExamModeWidget';

// ── Per-widget error boundary ───────────────────────────────────────
class WidgetErrorBoundary extends Component<
  { type: string; children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) {
      const msg = (this.state.error as Error).message;
      console.error('[WidgetRenderer] widget crashed', { type: this.props.type, error: this.state.error });
      return (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 12, padding: '20px 24px', textAlign: 'center' }}>
          <p style={{ fontWeight: 700, color: '#DC2626', margin: '0 0 4px', fontSize: 14 }}>This question couldn't load</p>
          <p style={{ color: '#6B7280', fontSize: 12, margin: 0 }}>({this.props.type}: {msg})</p>
        </div>
      );
    }
    return this.props.children;
  }
}

const NumberLineWidget = lazy(() => import('./NumberLine').then(m => ({ default: m.NumberLineWidget })));
const FractionBarWidget = lazy(() => import('./FractionBar').then(m => ({ default: m.FractionBarWidget })));
const FunctionGrapherWidget = lazy(() => import('./FunctionGrapher').then(m => ({ default: m.FunctionGrapherWidget })));
const TimelineWidget = lazy(() => import('./Timeline').then(m => ({ default: m.TimelineWidget })));
const LabelledDiagramWidget = lazy(() => import('./LabelledDiagram').then(m => ({ default: m.LabelledDiagramWidget })));
const MoleculeViewerWidget = lazy(() => import('./MoleculeViewer').then(m => ({ default: m.MoleculeViewerWidget })));
const VariableSliderWidget = lazy(() => import('./VariableSlider').then(m => ({ default: m.VariableSliderWidget })));
const AnnotatedTextWidget = lazy(() => import('./AnnotatedText').then(m => ({ default: m.AnnotatedTextWidget })));
const ClickableMapWidget = lazy(() => import('./ClickableMap').then(m => ({ default: m.ClickableMapWidget })));
const DecisionTreeWidget = lazy(() => import('./DecisionTree').then(m => ({ default: m.DecisionTreeWidget })));
const DragDropWidget = lazy(() => import('./DragDrop/DragDropWidget').then(m => ({ default: m.DragDropWidget })));
const InlineClozeWidget = lazy(() => import('./InlineCloze/InlineClozeWidget').then(m => ({ default: m.InlineClozeWidget })));
const HotSpotWidget = lazy(() => import('./HotSpot/HotSpotWidget').then(m => ({ default: m.HotSpotWidget })));
const ExtractMatchingWidget = lazy(() => import('./ExtractMatching/ExtractMatchingWidget').then(m => ({ default: m.ExtractMatchingWidget })));
const GapMatchWidget = lazy(() => import('./GapMatch/GapMatchWidget').then(m => ({ default: m.GapMatchWidget })));
const HotTextWidget = lazy(() => import('./HotText/HotTextWidget').then(m => ({ default: m.HotTextWidget })));
const ImageMCQWidget = lazy(() => import('./ImageMCQ/ImageMCQWidget').then(m => ({ default: m.ImageMCQWidget })));
const WorldMapWidget = lazy(() => import('./WorldMap').then(m => ({ default: m.WorldMapWidget })));
const Model3DViewerWidget = lazy(() => import('./Model3DViewer').then(m => ({ default: m.Model3DViewerWidget })));
const DnaHelixWidget = lazy(() => import('./DnaHelix').then(m => ({ default: m.DnaHelixWidget })));

const REGISTRY: Record<string, React.ComponentType<any>> = {
  NumberLine: NumberLineWidget,
  FractionBar: FractionBarWidget,
  FunctionGrapher: FunctionGrapherWidget,
  Timeline: TimelineWidget,
  LabelledDiagram: LabelledDiagramWidget,
  MoleculeViewer: MoleculeViewerWidget,
  VariableSlider: VariableSliderWidget,
  AnnotatedText: AnnotatedTextWidget,
  ClickableMap: ClickableMapWidget,
  DecisionTree: DecisionTreeWidget,
  DragDrop: DragDropWidget,
  InlineCloze: InlineClozeWidget,
  HotSpot: HotSpotWidget,
  ExtractMatching: ExtractMatchingWidget,
  GapMatch: GapMatchWidget,
  HotText2: HotTextWidget,
  ExamMode: ExamModeWidget,
  ImageMCQ: ImageMCQWidget,
  WorldMap: WorldMapWidget,
  Model3DViewer: Model3DViewerWidget,
  DnaHelix: DnaHelixWidget,
};

interface WidgetConfig {
  type: string;
  config: Record<string, unknown>;
  teaching_goal?: string;
  correct_solution_for_lumi?: string;
}

interface Props extends Omit<BaseWidgetProps, 'widgetId'> {
  widget: WidgetConfig;
  mode?: 'explore' | 'challenge' | 'quiz';
}

const WidgetFallback = () => (
  <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 14, padding: '24px', textAlign: 'center' }}>
    <div style={{ fontSize: 24, marginBottom: 8 }}>⚡</div>
    <p style={{ fontSize: 13, color: '#9CA3AF', margin: 0 }}>Loading widget…</p>
  </div>
);

export function WidgetRenderer({ widget, lessonId, mode = 'explore' }: Props) {
  if (!widget || widget.type === 'none') return null;
  const Component = REGISTRY[widget.type];
  if (!Component) {
    console.warn('[WidgetRenderer] unknown type — no widget registered for:', widget.type);
    return (
      <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 14, padding: '24px', textAlign: 'center' }}>
        <p style={{ fontSize: 13, color: '#9CA3AF', margin: 0 }}>This question type ({widget.type}) is loading…</p>
      </div>
    );
  }

  const widgetId = `${lessonId}_${widget.type}`;

  return (
    <div style={{ marginBottom: 24 }}>
      <WidgetErrorBoundary type={widget.type}>
        <Suspense fallback={<WidgetFallback />}>
          <Component config={widget.config} lessonId={lessonId} widgetId={widgetId} mode={mode} />
        </Suspense>
      </WidgetErrorBoundary>
      {widget.teaching_goal && mode === 'explore' && (
        <p style={{ fontSize: 11, color: '#9CA3AF', margin: '8px 0 0 4px', fontStyle: 'italic' }}>
          {widget.teaching_goal}
        </p>
      )}
    </div>
  );
}
